import React from "react"
import { useActiveObject, useEditor } from "@layerhub-io/react"
import getSelectionType from "~/utils/get-selection-type"
import { styled } from "baseui"
import Items from "./Items"
import useAppContext from "~/hooks/useAppContext"
import { ILayer } from "@layerhub-io/types"
import { Button, KIND } from "baseui/button"
import useDesignEditorContext from "~/hooks/useDesignEditorContext"
import useDesignEditorPages from "~/hooks/useDesignEditorScenes"
import { getDefaultTemplate } from "~/constants/design-editor"
import { nanoid } from "nanoid"
import Cookies from 'js-cookie'

const DEFAULT_TOOLBOX = "Canvas"

interface ToolboxState {
  toolbox: string
}

const Container = styled("div", (props) => ({
  boxShadow: "rgb(0 0 0 / 15%) 0px 1px 1px",
  height: "50px",
  display: "flex",
}))

const Toolbox = () => {
  const [state, setState] = React.useState<ToolboxState>({ toolbox: "Text" })
  const { setActiveSubMenu, skuData } = useAppContext()
  const activeObject = useActiveObject() as ILayer
  const editor = useEditor()
  const { showDottedLine, setShowDottedLine, currentScene, setCurrentScene, setScenes, currentDesign } = useDesignEditorContext()
  const scenes = useDesignEditorPages()
  const sku = Cookies.get('photobookSku') || ""
  const skuUpper = sku.toUpperCase()

  const coverFinish = (Cookies.get('cover_finish') || "").toLowerCase()
  const hasCover = sku.includes("PH") || coverFinish.includes("personalized") || coverFinish.includes("personalised")
  const coverOffset = hasCover ? 1 : 0
  const isLayflat = skuUpper.includes("LAY")

  const includedSpreadCount = Number(skuData?.standard_page_size || 0) || Number(skuData?.total_spreads_per_book || 0)
  const standardSceneCount = includedSpreadCount + coverOffset

  const canAddMoreScenes = () => {
    return scenes.length < (skuData?.total_spreads_per_book || 0) + coverOffset + 1
  }

  // Current scene is not the cover and total scenes exceed standard count
  const isCoverScene = hasCover && scenes.findIndex((s) => s.id === currentScene?.id) === 0
  const canDeleteCurrentScene = scenes.length > standardSceneCount && !isCoverScene && !!currentScene
  const shouldShowSpineGuideToggle = isLayflat || (hasCover && !isLayflat && isCoverScene)

  const deleteCurrentPage = React.useCallback(async () => {
    if (!currentScene || !canDeleteCurrentScene) return

    const currentIndex = scenes.findIndex((s) => s.id === currentScene.id)
    const updatedScenes = scenes.filter((s) => s.id !== currentScene.id)
    const nextIndex = Math.min(currentIndex, updatedScenes.length - 1)
    setCurrentScene(updatedScenes[nextIndex])
    setScenes(updatedScenes)
  }, [scenes, currentScene, canDeleteCurrentScene, skuData])

  const addPageAfterCurrent = React.useCallback(async () => {
    if (!editor || !currentScene) return

    const updatedTemplate = editor.scene.exportToJSON()

    const updatedPages = scenes.map((p) => {
      if (p.id === updatedTemplate.id) {
        return { ...updatedTemplate, preview: (p as any).preview }
      }
      return p
    })

    const defaultTemplate = getDefaultTemplate(currentDesign.frame)
    const newPreview = await editor.renderer.render(defaultTemplate)
    const items = [{
      ...defaultTemplate,
      id: nanoid(),
      preview: newPreview,
    } as any]

    // Always insert after the current scene
    const currentIndex = updatedPages.findIndex((p) => p.id === currentScene.id)
    const insertIndex = currentIndex !== -1 ? currentIndex + 1 : updatedPages.length
    const newPages = [
      ...updatedPages.slice(0, insertIndex),
      ...items,
      ...updatedPages.slice(insertIndex),
    ]
    setScenes(newPages)

    setCurrentScene(items[0] as any)
  }, [scenes, currentDesign, currentScene, editor, skuData])

  React.useEffect(() => {
    const selectionType = getSelectionType(activeObject)
    if (selectionType) {
      if (selectionType.length > 1) {
        setState({ toolbox: "Multiple" })
      } else {
        setState({ toolbox: selectionType[0] })
      }
    } else {
      setState({ toolbox: DEFAULT_TOOLBOX })
      setActiveSubMenu("")
    }
  }, [activeObject])

  React.useEffect(() => {
    let watcher = async () => {
      if (activeObject) {
        // @ts-ignore
        const selectionType = getSelectionType(activeObject) as any

        if (selectionType.length > 1) {
          setState({ toolbox: "Multiple" })
        } else {
          setState({ toolbox: selectionType[0] })
        }
      }
    }
    if (editor) {
      editor.on("history:changed", watcher)
    }
    return () => {
      if (editor) {
        editor.off("history:changed", watcher)
      }
    }
  }, [editor, activeObject])

  // @ts-ignore
  const Component = Items[state.toolbox]

  return (
    <Container>
      <div style={{ flex: 1, display: "flex", alignItems: "center" }}>
        {Component ? <Component /> : state.toolbox}
      </div>
      {canAddMoreScenes() && (
        <div style={{ display: "flex", alignItems: "center", paddingRight: "1rem" }}>
          <Button
            size="compact"
            kind={KIND.tertiary}
            onClick={addPageAfterCurrent}
          >
            Add Page
          </Button>
        </div>
      )}
      {canDeleteCurrentScene && (
        <div style={{ display: "flex", alignItems: "center", paddingRight: "1rem" }}>
          <Button
            size="compact"
            kind={KIND.tertiary}
            onClick={deleteCurrentPage}
          >
            Delete Page
          </Button>
        </div>
      )}
      {shouldShowSpineGuideToggle && (
        <div style={{ display: "flex", alignItems: "center", paddingRight: "1rem" }}>
          <Button
            size="compact"
            kind={KIND.tertiary}
            onClick={() => setShowDottedLine(!showDottedLine)}
          >
            {showDottedLine ? "Hide Spine Guide" : "Show Spine Guide"}
          </Button>
        </div>
      )}
    </Container>
  )
}

export default Toolbox
